import { dayjs } from "@thalamos/common";
import { InScopeNonSection136Detentions, MhaStatus, } from "../types/patientState/PatientState.js";
import { resolveDisplayedStatuses } from "./mhaStatusHierarchy.js";
/**
 * Pick which patient state row(s) the banner should render. Mirrors
 * `isInDualStatus` on the server side: dual is only triggered by a real
 * non-136 detention (Sections 17A/2/3/4/5_2/5_4) alongside an active
 * Section 136. Intermediary statuses (Unknown/Incomplete/Expired/
 * NotDetained) never count as the "other" side — a 136 patient with a
 * stub Unknown is single-status, not dual.
 *
 * Single-status prefers the Section 136 row, then a real surviving
 * non-136 detention, over an intermediary stub so an active detention
 * always wins display priority. This matters when a Section 136 has been
 * ended (e.g. via the outcome form, or an automatic expiry): the terminal
 * Section 136 row (NotDetained / Unknown / Incomplete / Expired) lingers as `isLatest`
 * alongside the surviving detention, and without this preference the
 * banner could surface that terminal row instead of the detention the
 * patient is actually still under. Falls back to the first row when no
 * detention is present (caller may further default to a synthetic Unknown
 * row when the list is empty).
 *
 * When neither a Section 136 nor a real non-136 detention is present (i.e. every
 * latest row is an intermediary — Not Detained / Incomplete / Expired), the
 * displayed status is chosen by the display hierarchy via
 * {@link resolveDisplayedStatuses} (Not Detained > Incomplete > Expired) rather
 * than an arbitrary first row.
 *
 * Only `isLatest` rows are considered: a status the patient has since moved on
 * from must never be picked for the banner. Callers happen to pre-filter to
 * latest rows today, but the invariant is enforced here so the primary
 * dual/single detection stays consistent with the {@link resolveDisplayedStatuses}
 * fallback (which filters `isLatest` itself) regardless of caller.
 */
export function selectSection136BannerStates(states, asOf = dayjs()) {
    const latest = states.filter((s) => s.isLatest);
    const section136 = latest.find((s) => s.state.mhaStatus.status === MhaStatus.Section136);
    const other = latest.find((s) => InScopeNonSection136Detentions.includes(s.state.mhaStatus.status));
    if (section136 && other) {
        return { kind: "dual", section136, other };
    }
    if (section136 ?? other) {
        return { kind: "single", state: section136 ?? other };
    }
    // No Section 136 and no real non-136 detention: every remaining row is an
    // intermediary, so defer to the hierarchy resolver to pick the winner
    // (Scenario 4) instead of returning whichever row the DB happened to sort
    // first. The resolver can only return a "single" here (no active detention
    // exists), and falls back to the first row when the list is empty.
    const resolved = resolveDisplayedStatuses(latest, asOf);
    return {
        kind: "single",
        state: resolved.kind === "single" ? resolved.state : latest[0],
    };
}
//# sourceMappingURL=section136Banner.js.map